<div class="page-wrapper">
  <main class="main-content">
    @if (partnerIneligible) {
      <!-- ── Declined. Checked before every other state on this page, and rendered with the
           payments flag on or off. ──
           A refund nulls `upfrontFeePaidAt`, which drops the user out of the already-paid state
           and would otherwise hand them "Pay $70 and continue to Abode" for a case Abode has
           already declined. So this branch replaces the page rather than sitting inside it:
           nothing here is actionable, and the offer copy, the price breakdown, the consent
           checkboxes, the payment element, the button and the deeplink are all absent.
           The amount binds to `totalFee`, never a literal, like every other figure on the
           page — copy that states a refunded amount must not drift from the charge. -->
      <p class="page-overline">FLORIDA PROPERTY TAX APPEAL</p>
      <p class="partner-ineligible">
        <strong>We're not able to move ahead with your appeal.</strong>
        After a closer look at your property, Abode Money wasn't able to move forward with an appeal. We've refunded your
        {{ totalFee | currency:'USD':'symbol':'1.0-0' }} in full — it should be back on your card within 5–10 business
        days.
      </p>
    } @else {
      <p class="page-overline">FLORIDA PROPERTY TAX APPEAL</p>
      <h1 class="page-title">Here is what we found for your home.</h1>

      <!-- Address, county and savings all come off the partner's lead row, never our own
           property record: the two systems can hold different properties for the same person. -->
      <div class="yours-card">
        <p class="yours-overline">Your property</p>
        @if (partnerAddress) {
          <p class="yours-address">{{ partnerAddress }}</p>
        }
        @if (countyName) {
          <p class="yours-county">{{ countyName }} County, Florida</p>
        }
        <!-- Guarded because the currency pipe renders null as an empty string. -->
        @if (estimatedSavings !== null) {
          <div class="yours-savings">
            <span class="yours-savings-value">{{ estimatedSavings | currency:'USD':'symbol':'1.0-0' }}</span>
            <span class="yours-savings-note">estimated savings are based on Abode's analysis of your property and available data. This is an estimate, not a guarantee.</span>
          </div>
        }
      </div>

      <!-- ── Offer copy. Two complete variants, switched by the same flag that gates the charge. ──
           Payments off is production today and must stay byte-for-byte identical: the contingency
           table, the contingency checkbox, "Agree and continue to Abode", and no mention of $70.
           Rendering the flat-fee copy with the flag off would promise a charge that never happens. -->
      @if (acceptPayments) {
        <div class="summary-card card">
          <dl class="summary-list">
            <dt>What happens</dt>
            <dd>Once Abode completes their full review, they prepare and file your petition with {{ countyQualifier }}
              Value Adjustment Board and handle the hearing on your behalf.</dd>
            <dt>What it costs</dt>
            <dd><strong>{{ totalFee | currency:'USD':'symbol':'1.0-0' }}, charged today.</strong> One flat fee, nothing
              else to pay later.</dd>
            <dt>What you keep</dt>
            <dd>Every dollar of any savings. We take no percentage.</dd>
            <!-- §6 of the linked terms: no petition filed, fees refunded in full. The amount binds to
                 `totalFee` like every other figure on the page so it cannot drift from the charge. -->
            <dt>If we don't file</dt>
            <dd>Abode's deeper review may find the evidence doesn't support filing. If that happens, we refund your
              {{ totalFee | currency:'USD':'symbol':'1.0-0' }} in full.</dd>
          </dl>
        </div>

        <p class="filing-fee-line">You would pay the county's filing fee no matter who files.</p>

        <!-- The total is the constant and the filing fee is the county's own, off the lead; the
             middle row is derived from the two, so the three figures cannot drift apart as the
             fee varies by county. The total stays $70 everywhere — only the split moves. -->
        <div class="breakdown">
          <p class="breakdown-overline">What your {{ totalFee | currency:'USD':'symbol':'1.0-0' }} covers</p>
          <div class="breakdown-line">
            <div class="breakdown-label">
              <strong>{{ countyQualifier | titleCase }} filing fee</strong>
              <span>What the county charges just to accept a petition. You would pay this yourself.</span>
            </div>
            <b class="breakdown-amount">{{ filingFee | currency:'USD':'symbol':'1.0-0' }}</b>
          </div>
          <div class="breakdown-line">
            <div class="breakdown-label">
              <strong>Everything else</strong>
              <span>The property review, comparable sales analysis, evidence package, the filing itself, and the
                hearing.</span>
            </div>
            <b class="breakdown-amount">{{ serviceFee | currency:'USD':'symbol':'1.0-0' }}</b>
          </div>
          <div class="breakdown-line breakdown-total">
            <div class="breakdown-label"><strong>Your total</strong></div>
            <b class="breakdown-amount">{{ totalFee | currency:'USD':'symbol':'1.0-0' }}</b>
          </div>
          <p class="breakdown-note">
            Filing it yourself would cost you the county's {{ filingFee | currency:'USD':'symbol':'1.0-0' }} and you would
            still have to pull comparable sales, build the evidence package, meet the exchange deadline, and appear at
            the hearing. Firms that do this work for you typically take 25% to 50% of whatever you save. We do not take a
            percentage at all.
          </p>
        </div>
      } @else {
        <p class="page-subtitle">Agree to the terms below and we will take it from there.</p>

        <div class="summary-card card">
          <dl class="summary-list">
            <dt>What happens</dt>
            <dd>Abode Money prepares and files your petition with {{ countyQualifier }} Value
              Adjustment Board, and handles the hearing on your behalf.</dd>
            <dt>Where it applies</dt>
            <dd>Florida properties only. This benefit is offered to Kin homeowners in Florida.</dd>
            <dt>If your tax bill goes down</dt>
            <dd>10% of your first year of savings, plus {{ countyQualifier }} filing fee of {{ contingencyFilingFee | currency:'USD':'symbol':'1.0-0' }}.</dd>
            <dt>If it does not</dt>
            <dd>Nothing at all, including no filing fee.</dd>
            <dt>When you are billed</dt>
            <dd>After your appeal is decided, and only if your bill went down.</dd>
          </dl>
        </div>
      }

      @if (!isReady) {
        <p class="not-ready">
          <strong>We're still setting up your appeal with Abode Money.</strong>
          We'll email you as soon as it's ready.
        </p>
      } @else {
        <!-- Payment step. Rendered only with `acceptPayments` on; with it off nothing here
             exists, no checkout session is requested, and Stripe.js is never fetched.
             It sits above the checkboxes and mounts on page load, so the user sees what they
             are paying for before they agree to it.
             An already-paid user never reaches `startPayment()`, so `showPayment` stays false
             and the whole block is absent — there is nothing left to collect from them. -->
        @if (acceptPayments && showPayment) {
          <div class="payment-step">
            <h2 class="payment-heading">Payment</h2>
            <p class="payment-sub">
              A one time charge of {{ totalFee | currency:'USD':'symbol':'1.0-0' }}.
              @if (receiptEmail) {
                We will email your receipt to {{ receiptEmail }}.
              } @else {
                We will email your receipt to you.
              }
            </p>
            <div #paymentElement class="payment-element"></div>
            <p class="payment-disclosure">
              Card details go straight to Stripe, our payment processor, and are never stored by Kin.
            </p>
          </div>
        }

        <div class="agree-checkbox-row">
          <ngk-checkbox [(value)]="agreedTerms">
            @if (acceptPayments) {
              <!-- stopPropagation: ngk-checkbox toggles from a host click listener; without this, opening the terms toggles consent. -->
              I agree to Kin's <a href="PROPERTY_TAX_CONSULTING_SERVICE_TERMS_FLORIDA.pdf" target="_blank" rel="noopener"
                 (click)="$event.stopPropagation()">Property Tax Appeal Terms</a> and authorize a one time charge of
              <strong>{{ totalFee | currency:'USD':'symbol':'1.0-0' }}</strong>. I understand this fee pays for preparing
              and filing my appeal, that it does not depend on the outcome, and that it is not refunded if the county
              does not lower my value. If no petition is filed for my property, the
              {{ totalFee | currency:'USD':'symbol':'1.0-0' }} is refunded in full.
            } @else {
              <!-- stopPropagation: ngk-checkbox toggles from a host click listener; without this, opening the terms toggles consent. -->
              I agree to Kin's <a href="PROPERTY_TAX_CONSULTING_SERVICE_TERMS_FLORIDA.pdf" target="_blank" rel="noopener"
                 (click)="$event.stopPropagation()">Property Tax Appeal Terms</a>, including that if my appeal lowers my tax
              bill I will be billed 10% of my actual first year savings plus {{ countyQualifierFirstPerson }} filing fee of
              {{ contingencyFilingFee | currency:'USD':'symbol':'1.0-0' }}, after my appeal is decided. If my bill is not lowered, I
              owe nothing.
            }
          </ngk-checkbox>
          <!-- One element, deliberately outside the `acceptPayments` switch: the legal entity name is
               the one from the terms PDF and is correct in both variants. -->
          <ngk-checkbox [(value)]="agreedSharing">
            I authorize Kin to share my information with Abode Financial Holdings, Inc. d/b/a Abode Money so they can
            prepare, file, and manage my appeal.
          </ngk-checkbox>
        </div>

        <!-- Also disabled while a returned payment is still processing: this button confirms a
             charge, which for someone mid-payment would be a second charge. -->
        <button ngkButton color="secondary" mode="solid" size="md" class="partner-btn"
                [disabled]="!agreedTerms || !agreedSharing || saving || paying || preparingPayment || !!paymentPending"
                (click)="agreeAndContinue()">
          {{ buttonLabel }}
        </button>

        <p class="partner-btn-note">
          Next, Abode Money takes over: their terms, the authorization form, and a few details to confirm. About two
          minutes.
        </p>

        @if (saveError) {
          <p class="save-error">{{ saveError }}</p>
        }

        @if (paymentError) {
          <p class="save-error payment-error">{{ paymentError }}</p>
        }

        <!-- A payment that came back still processing: no redirect, and deliberately no pay
             button — they have already been charged for this session. -->
        @if (paymentPending) {
          <p class="payment-pending">{{ paymentPending }}</p>
        }
      }
    }
  </main>

  @if (showInterstitial) {
    <div class="interstitial-scrim">
      <div class="interstitial-modal">
        @if (acceptPayments) {
          <div class="interstitial-tick interstitial-tick--warning">
            <ngk-icon icon="triangle-alert" size="2rem" color="var(--color-warning-600)" />
          </div>
          <h2 class="interstitial-title">
            <span class="interstitial-title-lead">Payment received.</span> One step left.
          </h2>
          <p class="interstitial-body">
            Your receipt is on its way to {{ receiptEmail || 'you' }}.<br />
            Your appeal is not filed yet.
          </p>
          <p class="interstitial-body">
            Abode Money handles the filing.<br />
            They need two things from you:
          </p>
          <ol class="interstitial-list">
            <li>Confirm your information</li>
            <li>Sign the authorization form</li>
          </ol>
          <button ngkButton color="secondary" mode="solid" size="md" class="interstitial-cta" (click)="goToPartner()">
            Continue to Abode Money
          </button>
          <p class="interstitial-disclaimer">
            <strong>Finish through this link only.</strong> Going through Abode's site instead creates a second account and asks
            to charge you another $99. Your ${{ totalFee }} covers everything. If you close this before finishing,
            Abode can't file. To come back later, use the same link in your receipt email.
          </p>
        } @else {
          <div class="interstitial-tick">
            <ngk-icon icon="circle-check" size="2rem" color="var(--color-success-600)" />
          </div>
          <h2 class="interstitial-title">Thanks for confirming your enrollment.</h2>
          <p class="interstitial-body">
            We're sending you to Abode Money to finish setting up your appeal. They'll ask
            you to sign the authorization form, confirm a code they text you, and answer a few quick questions about
            your home.
          </p>
          <button ngkButton color="secondary" mode="solid" size="md" class="interstitial-cta" (click)="goToPartner()">
            Continue to Abode Money
          </button>
        }
      </div>
    </div>
  }
</div>
